/**
 * PushDiagnose — Diagnose-UI (temporär für Debugging)
 *
 * Zeigt für den eingeloggten User:
 *   - VAPID konfiguriert?
 *   - Wieviele aktive Subscriptions auf diesem Account?
 *   - Ist der eigene User aktuell als WS-online erkannt? (Push würde unterdrückt)
 *   - Browser-Permission-Status
 *   - Service-Worker registriert?
 *   - Test-Push-Button
 *
 * Wird im Chat-Panel als ausklappbares Panel ganz unten angezeigt.
 * Phase 1 — kann später entfernt werden, wenn Push stabil läuft.
 */

import React, { useEffect, useState } from 'react';
import { ChevronDown, Activity } from 'lucide-react';
import {
  fetchPushDiagnose,
  sendTestPush,
  getPushCapability,
  hasActivePushSubscription,
  type PushDiagnose as PushDiagnoseData,
  type PushCapability
} from '../../services/support/pushSubscriptionService';

/** Prüft ob ein Service Worker auf dem Scope "/" registriert ist. */
async function hasServiceWorkerRegistration(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return !!reg;
}

interface PushDiagnoseProps {
  enabled: boolean;
}

/**
 * Master-Switch: zeigt das Diagnose-Panel komplett aus oder an.
 * Auf `true` setzen, wenn man wieder hineinschauen / debuggen will —
 * der gesamte Code bleibt funktionsfähig, nur das UI ist ausgeblendet.
 */
const SHOW_PUSH_DIAGNOSE = false;

export function PushDiagnose(props: PushDiagnoseProps): React.ReactElement | null {
  // Master-Switch — wenn aus, gar nicht erst die innere Komponente rendern.
  // Dadurch entstehen weder useState/useEffect noch Netzwerk-Calls.
  if (!SHOW_PUSH_DIAGNOSE) return null;
  return <PushDiagnoseInternal {...props} />;
}

function PushDiagnoseInternal({ enabled }: PushDiagnoseProps): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<PushDiagnoseData | null>(null);
  const [capability, setCapability] = useState<PushCapability | null>(null);
  const [hasLocalSub, setHasLocalSub] = useState<boolean | null>(null);
  const [hasSwReg, setHasSwReg] = useState<boolean | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    try {
      const [diag, cap, has, swReg] = await Promise.all([
        fetchPushDiagnose(),
        Promise.resolve(getPushCapability()),
        hasActivePushSubscription(),
        hasServiceWorkerRegistration()
      ]);
      setData(diag);
      setCapability(cap);
      setHasLocalSub(has);
      setHasSwReg(swReg);
    } catch (err) {
      console.error('[PushDiagnose] refresh failed', err);
    }
  };

  useEffect(() => {
    if (!enabled || !open) return;
    void refresh();
  }, [enabled, open]);

  const handleTest = async (): Promise<void> => {
    setIsTesting(true);
    setTestResult(null);
    try {
      await sendTestPush();
      setTestResult('Test-Push gesendet — sollte in den nächsten Sekunden auf deinem Gerät erscheinen.');
    } catch (err) {
      setTestResult(
        'Fehler beim Senden: ' + (err instanceof Error ? err.message : 'unbekannt')
      );
    } finally {
      setIsTesting(false);
    }
  };

  if (!enabled) return null;

  return (
    <div className="border-t border-white/5 dark:border-white/5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="
          w-full px-3 py-2
          flex items-center gap-2
          text-[10px] uppercase tracking-wider opacity-50 hover:opacity-100
          font-mono
          transition-opacity duration-150
        "
      >
        <Activity className="w-3 h-3" strokeWidth={2} />
        <span className="flex-1 text-left">Push-Diagnose</span>
        <ChevronDown
          className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <div className="px-3 pb-3 font-mono text-[11px] space-y-2">
          {!data || !capability ? (
            <p className="opacity-50">Lädt …</p>
          ) : (
            <>
              <Row
                label="Browser unterstützt Push"
                value={capability.isSupported ? 'ja' : 'nein'}
                ok={capability.isSupported}
              />
              {/* Detail-Aufschlüsselung wenn Browser-Support fehlt */}
              {!capability.isSupported && (
                <div className="ml-3 space-y-1 border-l border-white/10 pl-3 py-1">
                  <Row
                    label="serviceWorker API"
                    value={capability.hasServiceWorker ? 'ja' : 'nein'}
                    ok={capability.hasServiceWorker}
                  />
                  <Row
                    label="PushManager API"
                    value={capability.hasPushManager ? 'ja' : 'nein'}
                    ok={capability.hasPushManager}
                  />
                  <Row
                    label="Notification API"
                    value={capability.hasNotification ? 'ja' : 'nein'}
                    ok={capability.hasNotification}
                  />
                  <Row
                    label="Sicherer Kontext (HTTPS)"
                    value={capability.isSecureContext ? 'ja' : 'nein'}
                    ok={capability.isSecureContext}
                  />
                </div>
              )}
              {capability.isIOS && (
                <Row
                  label="PWA-Modus (iOS-Pflicht)"
                  value={capability.isStandalone ? 'aktiv' : 'inaktiv'}
                  ok={capability.isStandalone}
                />
              )}
              <Row
                label="Berechtigung"
                value={capability.permission}
                ok={capability.permission === 'granted'}
              />
              <Row
                label="Service Worker registriert"
                value={hasSwReg === null ? '…' : hasSwReg ? 'ja' : 'nein'}
                ok={hasSwReg === true}
              />
              <Row
                label="Push-Sub auf diesem Gerät"
                value={hasLocalSub === null ? '…' : hasLocalSub ? 'ja' : 'nein'}
                ok={hasLocalSub === true}
              />
              <Row
                label="Backend VAPID konfiguriert"
                value={data.vapidConfigured ? 'ja' : 'nein'}
                ok={data.vapidConfigured}
              />
              <Row
                label="Subscriptions in DB (aktiv)"
                value={String(data.activeSubscriptionCount)}
                ok={data.activeSubscriptionCount > 0}
              />
              <Row
                label="Aktuell WS-online?"
                value={data.wsOnline ? 'ja' : 'nein'}
                // Reines Info-Feld — Push wird unabhängig vom WS-Status
                // gesendet (Orchestrator schickt immer parallel zum WS-Event).
                ok={true}
              />

              <button
                type="button"
                onClick={handleTest}
                disabled={isTesting || data.activeSubscriptionCount === 0}
                className="
                  mt-2 w-full
                  text-[11px] px-3 py-2 rounded-lg
                  bg-brand text-gray-950 font-semibold
                  hover:bg-brand-hover
                  transition-colors duration-150
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isTesting ? 'Sendet …' : 'Test-Push an mich senden'}
              </button>

              {testResult && (
                <p className="opacity-70 mt-1 leading-relaxed">{testResult}</p>
              )}

              <button
                type="button"
                onClick={refresh}
                className="
                  text-[10px] uppercase tracking-wider opacity-40 hover:opacity-80
                  transition-opacity duration-150
                "
              >
                ↻ Aktualisieren
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const Row: React.FC<{ label: string; value: string; ok: boolean }> = ({ label, value, ok }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="opacity-60">{label}</span>
    <span className={ok ? 'text-emerald-400' : 'text-amber-400'}>{value}</span>
  </div>
);
